import { ReactNode } from "react";

type Props = {
  tag?: string;
  title: ReactNode;
  intro?: ReactNode;
  children?: ReactNode;
  panBackdrop?: boolean;
};

export function PageShell({ tag, title, intro, children, panBackdrop = true }: Props) {
  return (
    <div className="min-h-screen text-foreground relative">
      <section className="mx-auto max-w-5xl px-5 md:px-6 pt-28 md:pt-40 pb-12 md:pb-16">
        {tag && <div className="text-[10px] uppercase tracking-[0.5em] text-primary/90">{tag}</div>}
        <h1 className="mt-6 font-display italic text-4xl sm:text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95]">
          {title}
        </h1>
        {intro && (
          <p className="mt-8 text-lg md:text-xl text-foreground/70 max-w-2xl leading-relaxed">{intro}</p>
        )}
      </section>
      {children && (
        <section className="mx-auto max-w-5xl px-5 md:px-6 pb-20 md:pb-32">{children}</section>
      )}
    </div>
  );
}

export function PanBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,color-mix(in_oklab,var(--copper)_18%,transparent),transparent_52%)]" />
      <div className="absolute inset-0 bg-gradient-to-b from-background/45 via-background/70 to-background" />
    </div>
  );
}

export function Card({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-border/40 bg-card/40 backdrop-blur-sm p-8 hover:border-primary/40 transition-colors">
      {children}
    </div>
  );
}

const WSIMG_BASE =
  "https://img1.wsimg.com/isteam/ip/cc067f05-785a-4de6-a400-eaada90fa319";

export function PanImage({
  id,
  alt,
  className = "",
  width = 1200,
}: {
  id: string;
  alt: string;
  className?: string;
  width?: number;
}) {
  const src = `${WSIMG_BASE}/${encodeURI(id)}/:/rs=w:${width},m`;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`w-full h-full object-cover ${className}`}
    />
  );
}

export function YouTubeEmbed({ id, title }: { id: string; title: string }) {
  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-border/40 bg-black aspect-video">
      <iframe
        src={`https://www.youtube.com/embed/${id}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        loading="lazy"
        className="absolute inset-0 w-full h-full"
      />
    </div>
  );
}

export function SoundCloudEmbed({ url, title }: { url: string; title: string }) {
  const src = `https://w.soundcloud.com/player/?url=${encodeURIComponent(
    url
  )}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true`;
  return (
    <div className="overflow-hidden rounded-2xl border border-border/40 bg-card/30">
      <iframe
        src={src}
        title={title}
        loading="lazy"
        scrolling="no"
        allow="autoplay"
        className="w-full"
        height={420}
        style={{ border: 0 }}
      />
    </div>
  );
}
